import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Typography,
  Box,
  Chip,
} from '@mui/material';
import { PopupStoresCrawlService, PopupStoreCrawl } from '../../services/popupStoresCrawl.service';

interface Props {
  id: number;
  onClose: () => void;
  onApprove: (id: number) => void;
  onReject: (id: number) => void;
  onRefresh: () => void;
}

export default function CrawlDetailModal({ id, onClose, onApprove, onReject, onRefresh }: Props) {
  const [data, setData] = useState<PopupStoreCrawl | null>(null);

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    try {
      const response = await PopupStoresCrawlService.getOne(id);
      setData(response.data);
    } catch (error) {
      console.error('Failed to load crawl detail:', error);
    }
  };

  if (!data) return null;

  const processedData = data.processedData || {};
  const rawData = data.rawData || {};

  const handleAction = async (action: 'approve' | 'reject') => {
    if (action === 'approve') {
      await onApprove(id);
    } else {
      await onReject(id);
    }
    onClose();
    onRefresh();
  };

  return (
    <Dialog
      open={true}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      sx={{ zIndex: 10000 }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">{processedData.title || rawData.title}</Typography>
          <Chip
            label={
              data.status === 'approved'
                ? '승인됨'
                : data.status === 'rejected'
                ? '거부됨'
                : '대기중'
            }
            color={
              data.status === 'approved'
                ? 'success'
                : data.status === 'rejected'
                ? 'error'
                : 'default'
            }
          />
        </Box>
      </DialogTitle>

      <DialogContent>
        <Grid container spacing={2}>
          {/* 이미지 */}
          {processedData.thumbnail_image && (
            <Grid item xs={12}>
              <img
                src={processedData.thumbnail_image}
                alt="Thumbnail"
                style={{ width: '100%', maxHeight: 300, objectFit: 'cover' }}
              />
            </Grid>
          )}

          {/* 기본 정보 */}
          <Grid item xs={6}>
            <Typography variant="subtitle2">분류</Typography>
            <Typography>{processedData.place_type}</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="subtitle2">카테고리</Typography>
            <Typography>{processedData.category}</Typography>
          </Grid>

          <Grid item xs={12}>
            <Typography variant="subtitle2">주소</Typography>
            <Typography>{processedData.address}</Typography>
          </Grid>

          <Grid item xs={6}>
            <Typography variant="subtitle2">시작일</Typography>
            <Typography>{processedData.start_date}</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="subtitle2">종료일</Typography>
            <Typography>{processedData.end_date}</Typography>
          </Grid>

          {/* 설명 */}
          <Grid item xs={12}>
            <Typography variant="subtitle2">설명</Typography>
            <Box sx={{ whiteSpace: 'pre-wrap', mt: 1, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
              {processedData.description}
            </Box>
          </Grid>

          {/* 링크 */}
          {processedData.official_link && (
            <Grid item xs={12}>
              <Typography variant="subtitle2">공식 링크</Typography>
              <a href={processedData.official_link} target="_blank" rel="noopener noreferrer">
                {processedData.official_link}
              </a>
            </Grid>
          )}

          {processedData.instagram_link && (
            <Grid item xs={12}>
              <Typography variant="subtitle2">인스타그램</Typography>
              <a href={processedData.instagram_link} target="_blank" rel="noopener noreferrer">
                {processedData.instagram_link}
              </a>
            </Grid>
          )}

          {/* 출처 정보 */}
          <Grid item xs={12}>
            <Typography variant="subtitle2">출처</Typography>
            <Box sx={{ mt: 1 }}>
              <Chip
                label={data.crawlSource === 'popply' ? '팝플리 (Popply)' : '팝가 (Popga)'}
                color={data.crawlSource === 'popply' ? 'primary' : 'secondary'}
                variant="outlined"
              />
            </Box>
          </Grid>

          {data.sourceId && (
            <Grid item xs={12}>
              <Typography variant="subtitle2">원본 URL</Typography>
              <a href={data.sourceId} target="_blank" rel="noopener noreferrer">
                {data.sourceId}
              </a>
            </Grid>
          )}

          {/* 거부 사유 (있는 경우) */}
          {data.rejectReason && (
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="error">
                거부 사유
              </Typography>
              <Typography color="error">{data.rejectReason}</Typography>
            </Grid>
          )}
        </Grid>
      </DialogContent>

      <DialogActions>
        {data.status === 'pending' && (
          <>
            <Button color="success" variant="contained" onClick={() => handleAction('approve')}>
              승인
            </Button>
            <Button color="error" variant="contained" onClick={() => handleAction('reject')}>
              거부
            </Button>
          </>
        )}
        <Button onClick={onClose}>닫기</Button>
      </DialogActions>
    </Dialog>
  );
}
